/**
 * Navigation tree per PDS §1 IA.
 * Sections: Home, Identify, Deploy, Govern, Measure, Compliance, Vendors, Reports, Commercial, Settings.
 *
 * Each section / child carries a `surface` ID that maps onto the RBAC
 * permission matrix (`lib/rbac.js`). The sidebar uses this to filter
 * nav for the active persona — no nav code knows about personas directly.
 */
import {
  Home,
  Target,
  GitBranch,
  ShieldCheck,
  LineChart,
  ScrollText,
  Plug,
  FileSpreadsheet,
  DollarSign,
  Settings,
} from "lucide-react";

export const NAV_SECTIONS = [
  {
    id: "home",
    label: "Home",
    icon: Home,
    surface: "home",
    to: "/dashboard",
  },
  {
    id: "identify",
    label: "Identify",
    icon: Target,
    surface: "identify",
    children: [
      { id: "opportunities", label: "Opportunity Radar", surface: "identify", to: "/identify/opportunities" },
    ],
  },
  {
    id: "deploy",
    label: "Deploy",
    icon: GitBranch,
    surface: "deploy",
    // badge count is computed dynamically by Sidebar (= in-flight request count)
    children: [
      { id: "requests", label: "Deployment Requests", surface: "deploy", to: "/deploy/requests" },
    ],
  },
  {
    id: "govern",
    label: "Govern",
    icon: ShieldCheck,
    surface: "govern.agents", // visible when *any* govern child is allowed; resolved in Sidebar
    children: [
      { id: "agents",    label: "Agent Inventory",    surface: "govern.agents",    to: "/govern/agents" },
      { id: "policies",  label: "Policy Management",  surface: "govern.policies",  to: "/govern/policies" },
      { id: "hitl",      label: "Oversight Console",  surface: "govern.oversight", to: "/govern/oversight" },
      { id: "incidents", label: "Incident Register",  surface: "govern.incidents", to: "/govern/incidents" },
    ],
  },
  {
    id: "measure",
    label: "Measure",
    icon: LineChart,
    surface: "measure",
    children: [
      { id: "performance", label: "Performance & Drift", surface: "measure", to: "/measure/performance" },
      { id: "cost",        label: "Cost Optimisation",   surface: "measure", to: "/measure/cost" },
      { id: "benchmark",   label: "Benchmarking",        surface: "measure", to: "/measure/benchmark" },
    ],
  },
  {
    id: "compliance",
    label: "Compliance",
    icon: ScrollText,
    surface: "compliance",
    to: "/compliance",
  },
  {
    id: "vendors",
    label: "Vendors",
    icon: Plug,
    surface: "vendors",
    children: [
      { id: "integrations", label: "Integration Hub",    surface: "vendors", to: "/vendors/integrations" },
      { id: "performance",  label: "Vendor Performance", surface: "vendors", to: "/vendors/performance" },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: FileSpreadsheet,
    surface: "reports",
    children: [
      { id: "packs",     label: "Pre-Built Packs", surface: "reports", to: "/reports/packs" },
      { id: "builder",   label: "Custom Builder",  surface: "reports", to: "/reports/builder" },
      { id: "scheduled", label: "Scheduled",       surface: "reports", to: "/reports/scheduled" },
      { id: "library",   label: "Library",         surface: "reports", to: "/reports/library" },
    ],
  },
  {
    id: "commercial",
    label: "Commercial",
    icon: DollarSign,
    surface: "commercial",
    to: "/commercial",
  },
];

export const SETTINGS_SECTION = {
  id: "settings",
  label: "Settings",
  icon: Settings,
  surface: "settings.audit", // sidebar resolution picks any visible child
  children: [
    { id: "users", label: "Users & Roles", surface: "settings.users", to: "/settings/users" },
    { id: "audit", label: "Audit Log",     surface: "settings.audit", to: "/settings/audit" },
  ],
};
